import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  Button,
  Input,
  Select,
  Spinner,
  Textarea,
  makeStyles,
  tokens,
  Text,
  Badge,
  Label,
} from '@fluentui/react-components';
import { 
  CheckmarkRegular, 
  EditRegular, 
  ChevronDownRegular, 
  ChevronUpRegular,
  PersonRegular,
  MailRegular,
  PhoneRegular,
  BuildingRegular,
  ChatRegular,
  DismissRegular,
} from '@fluentui/react-icons';
import { getSharePointService } from '../services/sharePointService';
import { Volunteer, Comment as VolunteerComment, getBULeader, getTeamsForDepartment, TEAM_MANAGER_ROLE_BY_DEPARTMENT } from '../types/sharepoint';
import { useDepartmentDirectors } from '../hooks/useDepartmentDirectors';

// Canonical departments matching Org Chart (source of truth)
const CANONICAL_DEPARTMENTS = [
  'Marketing and Communications',
  'Clinical Operations',
  'Finance & Development',
  'LEHR',
  'Support Services',
  'Technology'
];

const useStyles = makeStyles({
  wideDialogSurface: {
    width: '1400px',
    maxWidth: '95vw',
  },
  modalContent: {
    padding: '32px 40px',
    maxHeight: '85vh',
    overflowY: 'auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    marginBottom: '32px',
    paddingBottom: '24px',
    borderBottom: `2px solid ${tokens.colorNeutralStroke2}`,
  },
  modalAvatar: {
    width: '96px',
    height: '96px',
    backgroundColor: tokens.colorBrandBackground,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '40px',
    fontWeight: tokens.fontWeightBold,
    color: 'white',
    flexShrink: 0,
    boxShadow: tokens.shadow8,
  },
  headerInfo: {
    flex: 1,
  },
  headerName: {
    fontSize: tokens.fontSizeBase600,
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorNeutralForeground1,
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  headerMeta: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  contentGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '24px',
    marginBottom: '24px',
    '@media (max-width: 1024px)': {
      gridTemplateColumns: '1fr',
    },
  },
  sectionCard: {
    padding: '24px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    boxShadow: tokens.shadow2,
  },
  sectionTitle: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorNeutralForeground1,
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    paddingBottom: '12px',
    borderBottom: `2px solid ${tokens.colorNeutralStroke2}`,
  },
  field: {
    marginBottom: '20px',
  },
  fieldLabel: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
    marginBottom: '8px',
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
  },
  fieldValue: {
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorNeutralForeground1,
    padding: '12px 16px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    minHeight: '40px',
    display: 'flex',
    alignItems: 'center',
  },
  fieldValueWithIcon: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  fieldIcon: {
    color: tokens.colorNeutralForeground3,
    fontSize: '18px',
  },
  servingAreasGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginTop: '12px',
  },
  additionalInfoCard: {
    padding: '20px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    marginBottom: '24px',
  },
  commentsCard: {
    padding: '20px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    boxShadow: tokens.shadow2,
  },
  commentsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    paddingBottom: '12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  commentsTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorNeutralForeground1,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  commentsCount: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
  },
  commentsList: {
    maxHeight: '200px',
    overflowY: 'auto',
    marginBottom: '16px',
    paddingRight: '8px',
  },
  commentItem: {
    padding: '12px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  commentAuthor: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    marginBottom: '4px',
  },
  commentDate: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginBottom: '6px',
  },
  commentContent: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    lineHeight: '1.5',
    wordBreak: 'break-word',
  },
  commentInputSection: {
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  emptyComments: {
    textAlign: 'center',
    padding: '24px',
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
    fontSize: tokens.fontSizeBase300,
  },
  newBadge: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground2,
    borderRadius: '12px',
    padding: '4px 10px',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
  },
});

type Props = {
  volunteer: Volunteer;
  onClose: () => void;
  onUpdated?: (volunteer: Volunteer) => void;
};

export const VolunteerDetailsModal: React.FC<Props> = ({ volunteer, onClose, onUpdated }) => {
  const styles = useStyles();
  const [selectedVolunteer, setSelectedVolunteer] = useState<Volunteer>(volunteer);
  const [isEditingContact, setIsEditingContact] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [comments, setComments] = useState<VolunteerComment[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentsExpanded, setCommentsExpanded] = useState(false); // Collapsed by default
  const [submittingComment, setSubmittingComment] = useState(false);
  const [roleToLead, setRoleToLead] = useState<Record<string, string>>({});
  const [allVolunteers, setAllVolunteers] = useState<Volunteer[]>([]);
  const { getDirectorsForDepartment, loading: loadingDirectors } = useDepartmentDirectors();
  
  // Load all volunteers to check director roles
  useEffect(() => {
    (async () => {
      try {
        const volunteers = await getSharePointService().getVolunteers();
        setAllVolunteers(volunteers);
      } catch (error) {
        console.error('Error loading volunteers:', error);
      }
    })();
  }, []);

  useEffect(() => {
    setSelectedVolunteer(volunteer);
  }, [volunteer]);

  const updateVolunteer = useCallback(async (id: string, updates: Partial<Volunteer>) => {
    try {
      setSelectedVolunteer(prev => ({ ...prev, ...updates }));
      await getSharePointService().updateVolunteer(id, updates);
      if (onUpdated) {
        onUpdated({ ...(selectedVolunteer as Volunteer), ...updates, id });
      }
    } catch (error) {
      // Reload fallback
    }
  }, [onUpdated, selectedVolunteer]);

  const loadComments = useCallback(async (volunteerId: string) => {
    try {
      setLoadingComments(true);
      const service = getSharePointService();
      const volunteerComments = await service.getVolunteerCommentsFromList(volunteerId);
      setComments(volunteerComments);
    } catch (error) {
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  }, []);

  const addVolunteerComment = useCallback(async (volunteerId: string, comment: string) => {
    const service = getSharePointService();
    await service.addVolunteerCommentToList(volunteerId, selectedVolunteer?.name || 'Unknown', comment);
  }, [selectedVolunteer]);

  useEffect(() => {
    if (selectedVolunteer?.id) {
      loadComments(selectedVolunteer.id);
    }
  }, [selectedVolunteer?.id, loadComments]);

  // Load all volunteers once to resolve team lead names by role
  useEffect(() => {
    (async () => {
      try {
        const data = await getSharePointService().getVolunteers();
        const map: Record<string, string> = {};
        data.forEach(v => {
          if (v.assignedRole && v.name && !map[v.assignedRole]) map[v.assignedRole] = v.name;
        });
        setRoleToLead(map);
      } catch {
        // ignore
      }
    })();
  }, []);

  const getTeamLeadDisplay = () => {
    const dept = selectedVolunteer?.businessUnit || '';
    const team = selectedVolunteer?.secondaryArea || '';
    const role = TEAM_MANAGER_ROLE_BY_DEPARTMENT[dept]?.[team];
    if (!role) return 'Not assigned';
    return roleToLead[role] || 'Not assigned';
  };

  const getDaysSinceJoined = () => {
    if (!selectedVolunteer?.createdAt) return null;
    try {
      const created = new Date(selectedVolunteer.createdAt as string);
      return Math.floor((Date.now() - created.getTime()) / (1000 * 60 * 60 * 24));
    } catch {
      return null;
    }
  };

  const isNewVolunteer = () => {
    const days = getDaysSinceJoined();
    return days !== null && days <= 90;
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogSurface className={styles.wideDialogSurface}>
        <DialogBody>
          <DialogTitle>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <span>Volunteer Details</span>
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                onClick={onClose}
                size="small"
              />
            </div>
          </DialogTitle>
          <DialogContent>
            <div className={styles.modalContent}>
              {/* Header */}
              <div className={styles.header}>
                <div className={styles.modalAvatar}>
                  {selectedVolunteer?.name?.charAt(0) || 'V'}
                </div>
                <div className={styles.headerInfo}>
                  <div className={styles.headerName}>
                    {selectedVolunteer?.name || 'Unknown Volunteer'}
                    {isNewVolunteer() && (
                      <Badge className={styles.newBadge}>NEW</Badge>
                    )}
                  </div>
                  <div className={styles.headerMeta}>
                    {selectedVolunteer?.businessUnit && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <BuildingRegular style={{ fontSize: '16px' }} />
                        <Text size={300}>{selectedVolunteer.businessUnit}</Text>
                      </div>
                    )}
                    {selectedVolunteer?.assignedRole && (
                      <Badge appearance="filled" size="small">
                        {selectedVolunteer.assignedRole}
                      </Badge>
                    )}
                    {selectedVolunteer?.createdAt && getDaysSinceJoined() !== null && (
                      <Text size={300} style={{ color: tokens.colorNeutralForeground3 }}>
                        Joined {getDaysSinceJoined()} day{getDaysSinceJoined() === 1 ? '' : 's'} ago
                      </Text>
                    )}
                  </div>
                </div>
              </div>

              {/* Main Content Grid */}
              <div className={styles.contentGrid}>
                {/* Left Column - Contact & Organization */}
                <div className={styles.sectionCard}>
                  <div className={styles.sectionTitle}>
                    <PersonRegular />
                    Contact & Organization
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <Text size={300} style={{ fontWeight: tokens.fontWeightSemibold }}>
                      Contact Information
                    </Text>
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={isEditingContact ? <CheckmarkRegular /> : <EditRegular />}
                      onClick={() => setIsEditingContact(!isEditingContact)}
                    >
                      {isEditingContact ? 'Save' : 'Edit'}
                    </Button>
                  </div>

                  {/* Email */}
                  <div className={styles.field}>
                    <Label className={styles.fieldLabel}>
                      <MailRegular style={{ marginRight: '6px', fontSize: '14px' }} />
                      Email
                    </Label>
                    {isEditingContact ? (
                      <Input
                        value={selectedVolunteer?.email || ''}
                        onChange={(e) => updateVolunteer(selectedVolunteer.id, { email: e.target.value })}
                        placeholder="Enter email address"
                        style={{ width: '100%' }}
                      />
                    ) : (
                      <div className={styles.fieldValue}>
                        {selectedVolunteer?.email || 'No email provided'}
                      </div>
                    )}
                  </div>

                  {/* Phone */}
                  <div className={styles.field}>
                    <Label className={styles.fieldLabel}>
                      <PhoneRegular style={{ marginRight: '6px', fontSize: '14px' }} />
                      Phone Number
                    </Label>
                    {isEditingContact ? (
                      <Input
                        value={selectedVolunteer?.phone || ''}
                        onChange={(e) => {
                          const phoneValue = e.target.value;
                          updateVolunteer(selectedVolunteer.id, { phone: phoneValue, whatsapp: phoneValue });
                        }}
                        placeholder="Enter phone number"
                        style={{ width: '100%' }}
                      />
                    ) : (
                      <div className={styles.fieldValue}>
                        {selectedVolunteer?.phone || 'No phone number provided'}
                      </div>
                    )}
                  </div>

                  {/* Department Directors */}
                  <div className={styles.field}>
                    <Label className={styles.fieldLabel}>
                      <BuildingRegular style={{ marginRight: '6px', fontSize: '14px' }} />
                      Department Director{loadingDirectors ? '' : (getDirectorsForDepartment(selectedVolunteer?.businessUnit).length > 1 ? 's' : '')}
                    </Label>
                    <div className={styles.fieldValue}>
                      {loadingDirectors ? (
                        'Loading...'
                      ) : (() => {
                        const directors = getDirectorsForDepartment(selectedVolunteer?.businessUnit);
                        if (directors.length > 0) {
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              {directors.map((name, idx) => {
                                const directorVolunteer = allVolunteers.find(v => v.name === name);
                                const isBoardMember = directorVolunteer?.assignedRole === 'Board Member' || false;
                                return (
                                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span>{name}</span>
                                    {isBoardMember && (
                                      <Badge size="small" appearance="filled">Board</Badge>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        }
                        return getBULeader(selectedVolunteer?.businessUnit) || 'Not assigned';
                      })()}
                    </div>
                  </div>

                  {/* Team Lead */}
                  <div className={styles.field}>
                    <Label className={styles.fieldLabel}>Department Lead</Label>
                    <div className={styles.fieldValue}>
                      {getTeamLeadDisplay()}
                    </div>
                  </div>
                </div>

                {/* Right Column - Serving Areas */}
                <div className={styles.sectionCard}>
                  <div className={styles.sectionTitle}>
                    <BuildingRegular />
                    Serving Areas & Role
                  </div>

                  <div className={styles.servingAreasGrid}>
                    {/* Department */}
                    <div className={styles.field}>
                      <Label className={styles.fieldLabel}>Department</Label>
                      <Select
                        value={selectedVolunteer?.businessUnit || ''}
                        onChange={(e, data) => updateVolunteer(selectedVolunteer.id, { businessUnit: data.value || '' })}
                        style={{ width: '100%' }}
                      >
                        <option value="">Select Department</option>
                        {CANONICAL_DEPARTMENTS.map(unit => (
                          <option key={unit} value={unit}>{unit}</option>
                        ))}
                      </Select>
                    </div>

                    {/* Clinic Day Area */}
                    <div className={styles.field}>
                      <Label className={styles.fieldLabel}>Clinic Day Area</Label>
                      <Select
                        value={selectedVolunteer?.primaryArea || ''}
                        onChange={(e, data) => updateVolunteer(selectedVolunteer.id, { primaryArea: data.value || '' })}
                        style={{ width: '100%' }}
                      >
                        <option value="">Select Clinic Day Area</option>
                        {['Pre-Ops','Ops Day Of','Greeters','Registration','Intake','Lab','Exam Rooms','Security','Prayer','Translator'].map(area => (
                          <option key={area} value={area}>{area}</option>
                        ))}
                      </Select>
                    </div>

                    {/* Team */}
                    <div className={styles.field}>
                      <Label className={styles.fieldLabel}>Team</Label>
                      <Select
                        value={selectedVolunteer?.secondaryArea || ''}
                        onChange={(e, data) => updateVolunteer(selectedVolunteer.id, { secondaryArea: data.value || '' })}
                        style={{ width: '100%' }}
                      >
                        <option value="">Select Team</option>
                        {getTeamsForDepartment(selectedVolunteer?.businessUnit).map(team => (
                          <option key={team} value={team}>{team}</option>
                        ))}
                      </Select>
                    </div>

                    {/* Role */}
                    <div className={styles.field}>
                      <Label className={styles.fieldLabel}>Role</Label>
                      <Select
                        value={selectedVolunteer?.assignedRole || ''}
                        onChange={(e, data) => updateVolunteer(selectedVolunteer.id, { assignedRole: data.value || '' })}
                        style={{ width: '100%' }}
                      >
                        <option value="">Select Role</option>
                        {['Board Member','Dept Lead','Department Lead','Volunteer'].map(role => (
                          <option key={role} value={role}>{role}</option>
                        ))}
                      </Select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              {selectedVolunteer?.additionalInformation && (
                <div className={styles.additionalInfoCard}>
                  <div className={styles.sectionTitle} style={{ marginBottom: '16px', borderBottom: 'none', paddingBottom: 0 }}>
                    Additional Information
                  </div>
                  <Text size={400} style={{ 
                    lineHeight: '1.7', 
                    color: tokens.colorNeutralForeground1,
                    whiteSpace: 'pre-wrap'
                  }}>
                    {selectedVolunteer.additionalInformation}
                  </Text>
                </div>
              )}

              {/* Comments - Compact Section */}
              <div className={styles.commentsCard}>
                <div className={styles.commentsHeader}>
                  <div className={styles.commentsTitle}>
                    <ChatRegular />
                    Team Comments
                    {comments.length > 0 && (
                      <Badge className={styles.commentsCount}>{comments.length}</Badge>
                    )}
                  </div>
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={commentsExpanded ? <ChevronUpRegular /> : <ChevronDownRegular />}
                    onClick={() => setCommentsExpanded(!commentsExpanded)}
                  >
                    {commentsExpanded ? 'Collapse' : 'Expand'}
                  </Button>
                </div>

                {commentsExpanded && (
                  <>
                    {loadingComments ? (
                      <div style={{ textAlign: 'center', padding: '24px' }}>
                        <Spinner size="small" label="Loading comments..." />
                      </div>
                    ) : comments.length > 0 ? (
                      <div className={styles.commentsList}>
                        {comments.map((comment) => (
                          <div key={comment.id} className={styles.commentItem}>
                            <div className={styles.commentAuthor}>
                              {comment.author}
                            </div>
                            <div className={styles.commentDate}>
                              {new Date(comment.timestamp).toLocaleDateString()} at {new Date(comment.timestamp).toLocaleTimeString()}
                            </div>
                            <div className={styles.commentContent}>
                              {comment.content}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className={styles.emptyComments}>
                        No comments yet. Add the first comment below.
                      </div>
                    )}

                    <div className={styles.commentInputSection}>
                      <Textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Add a comment about this volunteer..."
                        rows={3}
                        style={{ marginBottom: '12px' }}
                        disabled={submittingComment}
                      />
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <Button
                          appearance="subtle"
                          size="small"
                          onClick={() => setNewComment('')}
                          disabled={submittingComment}
                        >
                          Clear
                        </Button>
                        <Button
                          appearance="primary"
                          size="small"
                          onClick={async () => {
                            if (!selectedVolunteer || !newComment.trim()) return;
                            setSubmittingComment(true);
                            try {
                              await addVolunteerComment(selectedVolunteer.id, newComment.trim());
                              setNewComment('');
                              await loadComments(selectedVolunteer.id);
                            } finally {
                              setSubmittingComment(false);
                            }
                          }}
                          disabled={!newComment.trim() || submittingComment}
                        >
                          {submittingComment ? <Spinner size="tiny" /> : 'Add Comment'}
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
                <Button appearance="secondary" onClick={onClose}>Close</Button>
              </div>
            </div>
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default VolunteerDetailsModal;
